import {
  BookkeepingProgressionInfo as BookkeepingProgressionInfoProto,
  WorkItemCompletion as WorkItemCompletionProto,
  BookkeepingCycle as BookkeepingCycleProto,
  CycleCorrection as CycleCorrectionProto,
  PlatformConnection as PlatformConnectionProto,
  BookkeepingStage as BookkeepingStageProto,
  AccessStatus as AccessStatusProto,
  AssessmentStatus as AssessmentStatusProto,
  AssessmentReviewStatus as AssessmentReviewStatusProto,
  WorkItem as WorkItemProto,
} from '@taxfyle/api-internal/internal/bookkeeping_pb'
import { Model } from 'infra/model'
import { once } from 'lodash'
import { computed, makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { maybeParseDate } from 'utils/date-util'
import { timestampToISO } from 'utils/grpc-util'
import { match } from 'utils/pattern-match-util'
import {
  BookkeepingPlatformConnection,
  QuickBooksAccount,
  XeroAccount,
} from './BookkeepingPlatformConnection'

export enum BookkeepingStage {
  BookkeepingStageUnspecified = 0,
  Pending = 1,
  ProvisioningAccess = 2,
  AssessingBooks = 3,
  ReadyToStart = 4,
  CycleInProgress = 5,
  Completed = 6,
  ScopeExtensionRequested = 7,
}

export enum AccessStatus {
  AccessStatusUnspecified = 0,
  NotRequested = 1,
  ManagedAccessClientConnectionRequested = 2,
  ManagedAccessClientConfirmationRequested = 3,
  ManagedAccessAdminConfirmationRequested = 4,
  ManagedAccessConfirmed = 5,
  UnmanagedAccessProviderConfirmationRequested = 6,
  UnmanagedAccessConfirmed = 7,
}

export enum AssessmentStatus {
  AssessmentStatusUnspecified = 0,
  NoAssessment = 1,
  Requested = 2,
  Submitted = 3,
}

export enum AssessmentReviewStatus {
  AssessmentReviewStatusUnspecified = 0,
  ReviewNotApplicable = 1,
  ReviewRequested = 2,
  Reviewed = 3,
}

export class WorkItemCompletion extends Model<
  WorkItemCompletionProto.AsObject,
  WorkItemCompletion
> {
  @observable id = Id.none
  @observable completedCount = 0
  @observable name = ''
  @observable description = ''

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<WorkItemCompletionProto.AsObject>,
  ): Partial<WorkItemCompletion> {
    return {
      id: attrs.id,
      completedCount: attrs.completedCount,
      name: attrs.name,
      description: attrs.description,
    }
  }
}

export class BookkeepingCycleCompleted extends Model<
  BookkeepingCycleProto.CycleStatus.Completed.AsObject,
  BookkeepingCycleCompleted
> {
  @observable items: WorkItemCompletion[] = []
  @observable completedAt = new Date()
  @observable completedById = Id.none

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<BookkeepingCycleProto.CycleStatus.Completed.AsObject>,
  ): Partial<BookkeepingCycleCompleted> {
    return {
      items: attrs.itemsList
        ? attrs.itemsList.map((item) =>
            new WorkItemCompletion().set(item, {
              parse: true,
              stripUndefined: true,
            }),
          )
        : [],
      completedAt: maybeParseDate(timestampToISO(attrs.completedAt)),
      completedById: attrs.completedById,
    }
  }
}

export class BookkeepingCycleCanceled extends Model<
  BookkeepingCycleProto.CycleStatus.Canceled.AsObject,
  BookkeepingCycleCanceled
> {
  @observable canceledAt = new Date()

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<BookkeepingCycleProto.CycleStatus.Canceled.AsObject>,
  ): Partial<BookkeepingCycleCanceled> {
    return {
      canceledAt: maybeParseDate(timestampToISO(attrs.canceledAt)),
    }
  }
}

export class BookkeepingCycleInProgress {}

const BookkeepingCycleCorrectionLazyNone = once(
  () => new BookkeepingCycleCorrection(),
)

export class BookkeepingCycleCorrection extends Model<
  CycleCorrectionProto.AsObject,
  BookkeepingCycleCorrection
> {
  @observable correctedAt = new Date()
  @observable correctedById = Id.none
  @observable items: WorkItemCompletion[] = []

  constructor() {
    super()
    makeObservable(this)
  }

  parse(
    attrs: Partial<CycleCorrectionProto.AsObject>,
  ): Partial<BookkeepingCycleCorrection> {
    return {
      correctedAt: maybeParseDate(timestampToISO(attrs.correctedAt)),
      correctedById: attrs.correctedById,
      items: attrs.itemsList
        ? attrs.itemsList.map((item) =>
            new WorkItemCompletion().set(item, {
              parse: true,
              stripUndefined: true,
            }),
          )
        : [],
    }
  }

  static get none() {
    return BookkeepingCycleCorrectionLazyNone()
  }
}

const BookkeepingCycleStatusLazyNone = once(() => new BookkeepingCycleStatus())

export enum CycleStatusType {
  UnspecifiedCycleStatus,
  InProgress,
  Complete,
  Canceled,
}

export class BookkeepingCycleStatus extends Model<
  BookkeepingCycleProto.CycleStatus.AsObject,
  BookkeepingCycleStatus
> {
  @observable completed?: BookkeepingCycleCompleted
  @observable canceled?: BookkeepingCycleCanceled
  @observable inProgress?: BookkeepingCycleInProgress

  constructor() {
    super()
    makeObservable(this)
  }

  /**
   * The cycle status as an enum value.
   */
  get type() {
    if (this.completed) {
      return CycleStatusType.Complete
    }
    if (this.inProgress) {
      return CycleStatusType.InProgress
    }
    if (this.canceled) {
      return CycleStatusType.Canceled
    }
    return CycleStatusType.UnspecifiedCycleStatus
  }

  parse(
    attrs: Partial<BookkeepingCycleProto.CycleStatus.AsObject>,
  ): Partial<BookkeepingCycleStatus> {
    return {
      completed: attrs.completed
        ? new BookkeepingCycleCompleted().set(attrs.completed, {
            parse: true,
            stripUndefined: true,
          })
        : undefined,
      canceled: attrs.canceled
        ? new BookkeepingCycleCanceled().set(attrs.canceled, {
            parse: true,
            stripUndefined: true,
          })
        : undefined,
      inProgress: attrs.inProgress
        ? new BookkeepingCycleInProgress()
        : undefined,
    }
  }

  static get none() {
    return BookkeepingCycleStatusLazyNone()
  }
}

const BookkeepingCycleLazyNone = once(() => new BookkeepingCycle())

export class BookkeepingCycle extends Model<
  BookkeepingCycleProto.AsObject,
  BookkeepingCycle
> {
  @observable id = Id.none
  @observable number = 0
  @observable status = BookkeepingCycleStatus.none
  @observable startedAt = new Date()
  @observable correction?: BookkeepingCycleCorrection

  constructor() {
    super()
    makeObservable(this)
  }

  /**
   * Returns the bookkeeping cycle status as a friendly name.
   */
  @computed
  get cycleStatusName() {
    switch (this.status.type) {
      case CycleStatusType.InProgress:
        return 'In progress'
      case CycleStatusType.Complete:
        return 'Completed'
      case CycleStatusType.Canceled:
        return 'Canceled'
      case CycleStatusType.UnspecifiedCycleStatus:
        return 'Unspecified cycle status'
    }
  }

  /**
   * The date when the cycle was completed.
   */
  @computed
  get completedAt() {
    return this.status.completed?.completedAt
  }

  /**
   * Whether the cycle is complete.
   */
  @computed
  get isCompleted() {
    return this.status.type === CycleStatusType.Complete
  }

  /**
   * Returns the completed or corrected work items.
   */
  @computed
  get workItemsCompletion() {
    return this.correction
      ? this.correction.items
      : this.status.completed?.items
  }

  parse(
    attrs: Partial<BookkeepingCycleProto.AsObject>,
  ): Partial<BookkeepingCycle> {
    return {
      id: attrs.id,
      number: attrs.number,
      status: attrs.status
        ? new BookkeepingCycleStatus().set(attrs.status, {
            parse: true,
            stripUndefined: true,
          })
        : undefined,
      startedAt: maybeParseDate(timestampToISO(attrs.startedAt)),
      correction: attrs.correction
        ? new BookkeepingCycleCorrection().set(attrs.correction, {
            parse: true,
            stripUndefined: true,
          })
        : undefined,
    }
  }

  static get none() {
    return BookkeepingCycleLazyNone()
  }
}

export class WorkItem extends Model<WorkItemProto.AsObject, WorkItem> {
  @observable workItemId = Id.none
  @observable inventoryItemId = Id.none
  @observable inventoryItemName = ''
  @observable inventoryItemDescription = ''
  @observable quantity = 0
  @observable completedCount = 0

  constructor() {
    super()
    makeObservable(this)
  }

  @computed
  get remaining() {
    return this.quantity - this.completedCount
  }

  parse(attrs: Partial<WorkItemProto.AsObject>): Partial<WorkItem> {
    return {
      workItemId: attrs.workItemId,
      inventoryItemId: attrs.inventoryItemId,
      inventoryItemName: attrs.inventoryItemName,
      inventoryItemDescription: attrs.inventoryItemDescription,
      quantity: attrs.quantity,
      completedCount: attrs.completedCount,
    }
  }
}

const mapToBookkeepingStage = (stage: BookkeepingStageProto) =>
  match(stage, {
    [BookkeepingStageProto.BOOKKEEPING_STAGE_UNSPECIFIED]: () =>
      BookkeepingStage.BookkeepingStageUnspecified,
    [BookkeepingStageProto.PENDING]: () => BookkeepingStage.Pending,
    [BookkeepingStageProto.PROVISIONING_ACCESS]: () =>
      BookkeepingStage.ProvisioningAccess,
    [BookkeepingStageProto.ASSESSING_BOOKS]: () =>
      BookkeepingStage.AssessingBooks,
    [BookkeepingStageProto.READY_TO_START]: () => BookkeepingStage.ReadyToStart,
    [BookkeepingStageProto.CYCLE_IN_PROGRESS]: () =>
      BookkeepingStage.CycleInProgress,
    [BookkeepingStageProto.SCOPE_EXTENSION_REQUESTED]: () =>
      BookkeepingStage.ScopeExtensionRequested,
    [BookkeepingStageProto.COMPLETED]: () => BookkeepingStage.Completed,
    [match.DEFAULT]: () => BookkeepingStage.BookkeepingStageUnspecified,
  })

const mapToAccessStatus = (accessStatus: AccessStatusProto) =>
  match(accessStatus, {
    [AccessStatusProto.ACCESS_STATUS_UNSPECIFIED]: () =>
      AccessStatus.AccessStatusUnspecified,
    [AccessStatusProto.NOT_REQUESTED]: () => AccessStatus.NotRequested,
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONNECTION_REQUESTED]: () =>
      AccessStatus.ManagedAccessClientConnectionRequested,
    [AccessStatusProto.MANAGED_ACCESS_CLIENT_CONFIRMATION_REQUESTED]: () =>
      AccessStatus.ManagedAccessClientConfirmationRequested,
    [AccessStatusProto.MANAGED_ACCESS_ADMIN_CONFIRMATION_REQUESTED]: () =>
      AccessStatus.ManagedAccessAdminConfirmationRequested,
    [AccessStatusProto.MANAGED_ACCESS_CONFIRMED]: () =>
      AccessStatus.ManagedAccessConfirmed,
    [AccessStatusProto.UNMANAGED_ACCESS_PROVIDER_CONFIRMATION_REQUESTED]: () =>
      AccessStatus.UnmanagedAccessProviderConfirmationRequested,
    [AccessStatusProto.UNMANAGED_ACCESS_CONFIRMED]: () =>
      AccessStatus.UnmanagedAccessConfirmed,
    [match.DEFAULT]: () => AccessStatus.AccessStatusUnspecified,
  })

const mapToAssessmentStatus = (assessmentStatus: AssessmentStatusProto) =>
  match(assessmentStatus, {
    [AssessmentStatusProto.ASSESSMENT_STATUS_UNSPECIFIED]: () =>
      AssessmentStatus.AssessmentStatusUnspecified,
    [AssessmentStatusProto.NO_ASSESSMENT]: () => AssessmentStatus.NoAssessment,
    [AssessmentStatusProto.REQUESTED]: () => AssessmentStatus.Requested,
    [AssessmentStatusProto.SUBMITTED]: () => AssessmentStatus.Submitted,
    [match.DEFAULT]: () => AssessmentStatus.AssessmentStatusUnspecified,
  })

const mapToAssessmentReviewStatus = (
  assessmentReviewStatus: AssessmentReviewStatusProto,
) =>
  match(assessmentReviewStatus, {
    [AssessmentReviewStatusProto.ASSESSMENT_REVIEW_STATUS_UNSPECIFIED]: () =>
      AssessmentReviewStatus.AssessmentReviewStatusUnspecified,
    [AssessmentReviewStatusProto.REVIEW_REQUESTED]: () =>
      AssessmentReviewStatus.ReviewRequested,
    [AssessmentReviewStatusProto.REVIEWED]: () =>
      AssessmentReviewStatus.Reviewed,
    [AssessmentReviewStatusProto.REVIEW_NOT_APPLICABLE]: () =>
      AssessmentReviewStatus.ReviewNotApplicable,
    [match.DEFAULT]: () =>
      AssessmentReviewStatus.AssessmentReviewStatusUnspecified,
  })

function mapToPlatformConnection(
  platformConnection?: PlatformConnectionProto.AsObject,
): BookkeepingPlatformConnection | undefined {
  if (platformConnection?.quickBooks) {
    return new QuickBooksAccount().set(platformConnection.quickBooks)
  }

  if (platformConnection?.xero) {
    return new XeroAccount().set(platformConnection.xero)
  }

  return undefined
}

export default class BookkeepingProgressionInfo extends Model<
  BookkeepingProgressionInfoProto.AsObject,
  BookkeepingProgressionInfo
> {
  @observable id = Id.none
  @observable stage = BookkeepingStage.BookkeepingStageUnspecified
  @observable accessStatus = AccessStatus.AccessStatusUnspecified
  @observable cycles: BookkeepingCycle[] = []
  @observable platformConnection?: BookkeepingPlatformConnection
  @observable assessmentStatus = AssessmentStatus.AssessmentStatusUnspecified
  @observable assessmentReviewStatus =
    AssessmentReviewStatus.AssessmentReviewStatusUnspecified

  @observable workItems: WorkItem[] = []

  constructor() {
    super()
    makeObservable(this)
  }

  @computed
  get currentCycle() {
    return this.cycles.find((c) => c.status.type === CycleStatusType.InProgress)
  }

  parse(
    attrs: Partial<BookkeepingProgressionInfoProto.AsObject>,
  ): Partial<BookkeepingProgressionInfo> {
    return {
      id: attrs.id,
      stage: attrs.stage ? mapToBookkeepingStage(attrs.stage) : undefined,
      accessStatus: attrs.accessStatus
        ? mapToAccessStatus(attrs.accessStatus)
        : undefined,
      cycles: attrs.cyclesList
        ? attrs.cyclesList.map((c) =>
            new BookkeepingCycle().set(c, {
              parse: true,
              stripUndefined: true,
            }),
          )
        : undefined,
      platformConnection: mapToPlatformConnection(attrs.platformConnection),
      assessmentStatus: attrs.assessmentStatus
        ? mapToAssessmentStatus(attrs.assessmentStatus)
        : undefined,
      assessmentReviewStatus: attrs.assessmentReviewStatus
        ? mapToAssessmentReviewStatus(attrs.assessmentReviewStatus)
        : undefined,
      workItems: attrs.workItemsList
        ? attrs.workItemsList.map((wi) =>
            new WorkItem().set(wi, {
              parse: true,
              stripUndefined: true,
            }),
          )
        : undefined,
    }
  }
}
